import { useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { TbChevronRight } from "react-icons/tb";
import { AnimatePresence, motion } from "framer-motion";

import { SidebarNavItem } from "@/types/sidebar";
import { cn } from "@/lib/utils";

export interface SidebarNavProps {
  items: SidebarNavItem[];
}

interface SidebarNavItemsWithoutNestingProps {
  items: SidebarNavItem[];
  pathname: string | null;
}

interface SidebarNavItemsWithNestingProps {
  title: string;
  items: SidebarNavItem[];
  pathname: string | null;
}

export const SidebarNav = ({ items }: SidebarNavProps) => {
  const location = useLocation();

  return items?.length ? (
    <div className="w-full px-3 py-2">
      {items.map((item, index) => (
        <div key={index} className="pb-4">
          <h4 className="mb-1 rounded-md px-2 py-1 text-sm font-semibold">
            {item.title}
          </h4>

          {item?.items?.length && (
            <SidebarNavItemsWithoutNesting
              items={item.items}
              pathname={location.pathname}
            />
          )}
        </div>
      ))}
    </div>
  ) : null;
};

export function SidebarNavItemsWithoutNesting({
  items,
  pathname,
}: SidebarNavItemsWithoutNestingProps) {
  // location.pathname
  return items?.length ? (
    <div className="grid grid-flow-row auto-rows-max text-sm gap-2">
      {items.map((item, index) => {
        return item?.items?.length ? (
          <SidebarNavItemsWithNesting
            title={item?.title}
            key={index}
            items={item?.items}
            pathname={pathname}
          />
        ) : (
          <Link
            to={item.href as string}
            key={index}
            className={cn(
              "group flex w-full items-center rounded-md px-4 py-1.5 transition-all duration-300",
              pathname === item.href
                ? "bg-primary/80 font-medium text-primary-foreground"
                : "text-primary/80 hover:bg-primary/20"
            )}
          >
            {item.title}
          </Link>
        );
      })}
    </div>
  ) : null;
}

export function SidebarNavItemsWithNesting({
  items,
  pathname,
  title,
}: SidebarNavItemsWithNestingProps) {
  const [open, setOpen] = useState(false);

  const container = {
    hidden: { height: 0 },
    show: {
      height: "auto",
      transition: {
        when: "beforeChildren",
        staggerChildren: 0.1,
      },
    },
    exit: {
      height: 0,
      transition: {
        when: "afterChildren",
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0 },
    show: { opacity: 1 },
    exit: { opacity: 0 },
  };

  const MotionLink = motion(Link);

  return items?.length ? (
    <div
      className="group flex flex-col w-full transition-all duration-300 "
      onClick={() => setOpen((prev) => !prev)}
    >
      <div className="flex items-center justify-between text-primary/70 hover:bg-primary/20 cursor-pointer transition-all duration-300 px-4 py-1.5 rounded-md">
        <span>{title}</span>

        <button>
          <TbChevronRight
            className={cn(
              "w-4 h-4 transition-all duration-200",
              open ? "rotate-90" : "rotate-0"
            )}
          />
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            className="border-l border-accent-foreground/70 pl-3 ml-5"
            variants={container}
            initial="hidden"
            animate="show"
            exit="exit"
          >
            {items?.map((item, index) => (
              <MotionLink
                to={item?.href as string}
                key={index}
                variants={itemVariants}
                className={cn(
                  "group flex items-center rounded-md px-4 py-1.5 transition-all duration-300",
                  pathname === item.href
                    ? "bg-primary/80 font-medium text-primary-foreground"
                    : "text-primary/80 hover:bg-primary/20"
                )}
              >
                {item.title}
              </MotionLink>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  ) : null;
}
